import { authClient } from '@frontend/api-clients/authClient';

export interface AiProfile {
	id: string;
	scope: 'personal' | 'project';
	ownerUserPk?: string;
	projectId?: string | null;
	ecosystemId?: string;
	name: string;
	provider: 'openai' | 'ollama';
	baseUrl: string;
	model: string;
	hasApiKey: boolean;
	createdAt?: string;
	updatedAt?: string;
}

async function aiFetch<T>(path: string, init?: RequestInit): Promise<T> {
	const token = authClient.getToken();
	const response = await fetch(path, {
		...init,
		headers: {
			'Content-Type': 'application/json',
			...(token ? { Authorization: `Bearer ${token}` } : {}),
			...(init?.headers || {}),
		},
	});
	const data = (await response.json()) as { message?: string } & T;
	if (!response.ok) {
		throw new Error(data.message || `Request failed (${response.status})`);
	}
	return data;
}

export function listAiProfiles(params: {
	ecosystemId?: string;
	projectId?: string;
}) {
	const search = new URLSearchParams();
	if (params.ecosystemId) search.set('ecosystemId', params.ecosystemId);
	if (params.projectId) search.set('projectId', params.projectId);
	return aiFetch<{
		available: boolean;
		profiles: AiProfile[];
		selectedProfileId: string | null;
		allowedOrigins: string[];
		message?: string;
	}>(`/api/ai/profiles?${search}`);
}

export function createAiProfile(body: Record<string, unknown>) {
	return aiFetch<{ profile: AiProfile }>('/api/ai/profiles', {
		method: 'POST',
		body: JSON.stringify(body),
	});
}

export function deleteAiProfile(id: string) {
	return aiFetch<{ ok: boolean }>(`/api/ai/profiles/${encodeURIComponent(id)}`, {
		method: 'DELETE',
	});
}

export function selectAiProfile(body: {
	ecosystemId: string;
	projectId: string;
	profileId: string;
}) {
	return aiFetch<{ ok: boolean; selectedProfileId: string }>('/api/ai/profiles/selection', {
		method: 'PUT',
		body: JSON.stringify(body),
	});
}

export function testAiProfile(id: string) {
	return aiFetch<{ ok: boolean; message: string }>(
		`/api/ai/profiles/${encodeURIComponent(id)}/test`,
		{ method: 'POST', body: '{}' }
	);
}

export function getAiSettings() {
	return aiFetch<{
		profilesAvailable: boolean;
		encryptionConfigured: boolean;
		databaseConfigured: boolean;
		allowedOrigins: string[];
	}>('/api/ai/settings');
}
